/** ASSISTANT DM — les réponses prêtes pour Instagram, et le simulateur pour les essayer.
 *  Servae prépare le message, le pro le colle : aucune connexion Meta nécessaire aujourd'hui. */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Save, Send, Sparkles } from "lucide-react";
import { api, messageErreur } from "../api";
import { Btn, Champ, EnTete, Micro, Squelette, Zone } from "../components/Bits";

const EXEMPLES = [
  "Coucou, tu as des dispos cette semaine ?",
  "C'est combien la pose gel ?",
  "Tu es où exactement ?",
  "Je peux déplacer mon rdv de vendredi ?",
  "Salut, tu prends les nouvelles clientes en ce moment ?",
];

export default function Assistant() {
  const [data, setData] = useState(null);
  const [charge, setCharge] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState(EXEMPLES[0]);
  const [prenom, setPrenom] = useState("Léa");
  const [fil, setFil] = useState([]);
  const [reflexion, setReflexion] = useState(false);

  const charger = useCallback(async () => {
    setCharge(true);
    try {
      const { data } = await api.get("/assistant/regles");
      setData(data);
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setCharge(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      await api.put("/assistant/regles", {
        regles: data.regles,
        ton: data.ton,
        signature: data.signature || null,
      });
      toast.success("Tes réponses sont enregistrées.");
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const tester = async () => {
    if (!message.trim()) return;
    const envoye = message.trim();
    setFil((f) => [...f, { qui: "client", texte: envoye }]);
    setMessage("");
    setReflexion(true);
    try {
      const { data } = await api.post("/assistant/tester", { message: envoye, prenom });
      setFil((f) => [...f, { qui: "pro", texte: data.reponse, source: data.source }]);
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setReflexion(false);
    }
  };

  if (charge) return <Squelette lignes={4} />;

  return (
    <div className="pb-8" data-testid="page-assistant">
      <EnTete
        micro="Assistant DM"
        titre="Tes réponses Instagram, déjà écrites"
        sous="Un mot-clé dans le message, et Servae prépare la réponse avec tes vraies places et tes vrais tarifs. Tu relis, tu colles."
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="assistant-canaux">
        {[
          ["Instagram", "Connexion officielle possible", "Compte professionnel requis (gratuit). Réponse automatique dans les 24 h après le message.", true],
          ["WhatsApp", "Connexion officielle possible", "L'API business est ouverte : c'est le canal le plus simple à automatiser vraiment.", true],
          ["TikTok", "Pas d'accès aux messages", "TikTok n'ouvre pas ses messages privés aux logiciels. Servae écrit, tu colles.", false],
          ["Snapchat", "Pas d'accès aux messages", "Réservé à quelques partenaires marques. Servae écrit, tu colles.", false],
        ].map(([nom, etat, note, ok]) => (
          <div
            key={nom}
            className="p-4"
            style={{
              background: "var(--surface)",
              border: `1px solid ${ok ? "var(--accent)" : "var(--border)"}`,
              borderRadius: "var(--radius-lg)",
            }}
          >
            <div className="text-[15px] font-semibold">{nom}</div>
            <div
              className="text-[12px] font-bold uppercase tracking-[0.08em] mt-1.5"
              style={{ color: ok ? "var(--accent)" : "var(--text-subtle)" }}
            >
              {etat}
            </div>
            <p className="text-[13px] mt-2" style={{ color: "var(--text-muted)" }}>
              {note}
            </p>
          </div>
        ))}
      </div>

      <div
        className="flex items-start gap-2.5 p-4 mt-6"
        style={{ background: "var(--accent-soft)", borderRadius: "var(--radius-lg)" }}
        data-testid="assistant-note"
      >
        <Sparkles size={16} strokeWidth={1.9} style={{ color: "var(--accent)", marginTop: 2, flexShrink: 0 }} />
        <p className="text-[13.5px]">
          Sur Instagram et WhatsApp, la réponse peut partir toute seule une fois le compte
          connecté (compte professionnel + validation de la plateforme). Sur TikTok et
          Snapchat, aucun logiciel n'a accès aux messages privés : Servae écrit le message,
          tu le colles en un tap. Dans tous les cas, tu ne rédiges plus rien.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 mt-8">
        {/* -------------------------------------------------- le simulateur */}
        <section>
          <Micro className="pb-2.5 mb-4 block" style={{ borderBottom: "1px solid var(--border)" }}>
            Le simulateur
          </Micro>

          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-3">
            {EXEMPLES.map((e) => (
              <button
                key={e}
                onClick={() => setMessage(e)}
                data-testid="assistant-exemple"
                className="shrink-0 h-8 px-3 text-[12.5px] font-semibold"
                style={{
                  borderRadius: "var(--radius-full)",
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  color: "var(--text-muted)",
                }}
              >
                {e.length > 30 ? `${e.slice(0, 30)}…` : e}
              </button>
            ))}
          </div>

          <div
            className="p-4 space-y-3 min-h-[230px]"
            data-testid="assistant-fil"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
            }}
          >
            {fil.length === 0 && (
              <p className="text-[13.5px]" style={{ color: "var(--text-subtle)" }}>
                Écris ce qu'un client t'enverrait. Tu verras exactement ce que Servae te propose
                de répondre.
              </p>
            )}
            {fil.map((m, i) => (
              <div key={i} className={m.qui === "pro" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className="max-w-[86%] p-3"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: m.qui === "pro" ? "var(--accent)" : "var(--surface-sunken)",
                    color: m.qui === "pro" ? "#fff" : "var(--text)",
                  }}
                >
                  <p className="text-[14px] whitespace-pre-line">{m.texte}</p>
                  {m.qui === "pro" && (
                    <div className="flex items-center gap-3 mt-2.5">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(m.texte);
                          toast.success("Message copié.");
                        }}
                        data-testid="assistant-copier"
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold"
                        style={{ color: "rgba(255,255,255,0.9)" }}
                      >
                        <Copy size={12} strokeWidth={2.2} /> Copier
                      </button>
                      <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.65)" }}>
                        {m.source}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {reflexion && (
              <p className="text-[13px]" style={{ color: "var(--text-subtle)" }}>
                Servae rédige…
              </p>
            )}
          </div>

          <div className="flex gap-2 mt-3">
            <input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && tester()}
              placeholder="Le message reçu…"
              data-testid="assistant-message"
              className="flex-1 h-11 px-3.5 text-[15px] outline-none focus:border-[var(--accent)]"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
              }}
            />
            <Btn icone={Send} onClick={tester} charge={reflexion} data-testid="assistant-envoyer">
              Voir
            </Btn>
          </div>
          <div className="mt-3 max-w-[220px]">
            <Champ
              label="Prénom du client (simulation)"
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              data-testid="assistant-prenom"
            />
          </div>
        </section>

        {/* ------------------------------------------------------ les règles */}
        <section>
          <Micro className="pb-2.5 mb-4 block" style={{ borderBottom: "1px solid var(--border)" }}>
            Tes réponses
          </Micro>
          <p className="text-[13px] mb-4" style={{ color: "var(--text-muted)" }}>
            Variables disponibles : {"{prenom}"}, {"{lien}"}, {"{dispos}"}, {"{tarifs}"},{" "}
            {"{zone}"}. Elles sont remplacées par tes vraies données au moment de répondre.
          </p>

          <div className="space-y-4">
            {data.regles.map((r, i) => (
              <div
                key={r.id}
                className="p-4"
                data-testid={`regle-${r.id}`}
                style={{
                  background: "var(--surface)",
                  border: `1px solid ${r.actif ? "var(--accent)" : "var(--border)"}`,
                  borderRadius: "var(--radius-lg)",
                }}
              >
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={r.actif}
                    data-testid={`regle-actif-${r.id}`}
                    onChange={(e) => {
                      const regles = [...data.regles];
                      regles[i] = { ...r, actif: e.target.checked };
                      setData({ ...data, regles });
                    }}
                    style={{ accentColor: "var(--accent)", height: 17, width: 17 }}
                  />
                  <span className="text-[15px] font-semibold capitalize">{r.id}</span>
                  <span className="text-[12px]" style={{ color: "var(--text-subtle)" }}>
                    {r.mots.slice(0, 4).join(", ")}…
                  </span>
                </label>
                <Zone
                  className="mt-3"
                  value={r.reponse}
                  onChange={(e) => {
                    const regles = [...data.regles];
                    regles[i] = { ...r, reponse: e.target.value };
                    setData({ ...data, regles });
                  }}
                  data-testid={`regle-reponse-${r.id}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-5 space-y-3">
            <Champ
              label="Signature (optionnelle)"
              value={data.signature || ""}
              onChange={(e) => setData({ ...data, signature: e.target.value })}
              placeholder="— Camille, Studio Onyx"
              data-testid="assistant-signature"
            />
            <Btn icone={Save} onClick={enregistrer} charge={envoi} data-testid="assistant-enregistrer">
              Enregistrer mes réponses
            </Btn>
          </div>

          <p className="text-[12.5px] mt-4 flex items-start gap-2" style={{ color: "var(--text-subtle)" }}>
            <Check size={14} strokeWidth={2.2} style={{ marginTop: 3, flexShrink: 0 }} />
            Si aucun mot-clé ne correspond, l'assistant rédige une réponse dans ton ton, à partir
            de tes vraies places et de tes vrais tarifs.
          </p>
        </section>
      </div>
    </div>
  );
}
